import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle, BarChart3, Briefcase, CalendarClock, CalendarDays, CalendarOff, Clock, FileText, Network,
  RefreshCw, UserPlus, Users, Wallet,
} from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Skeleton } from '../../components/Skeleton';
import { useAuthStore } from '../../store/authStore';
import { useRbacStore } from '../../store/rbacStore';
import { usePeopleDashboardStore, type PeopleDashboardData } from '../../store/peopleDashboardStore';
import { cn } from '../../lib/utils';
import { CelebrationsBanner } from '../../components/CelebrationsBanner';
import { HolidaysPanel, MyAssetsPanel, MyDayCards, MyWeekPanel } from './dashboard/MyDaySection';
import { OrgSection } from './dashboard/OrgSection';
import { TeamSection } from './dashboard/TeamSection';
import { Panel, SectionHeading, parseDay } from './dashboard/widgets';

/** Silent refresh so "present today" and approval counts stay current. */
const REFRESH_MS = 5 * 60_000;

const VIEW_LABEL: Record<PeopleDashboardData['view'], string> = {
  org: 'Company-wide view',
  team: 'Team view',
  self: 'My workspace',
};

/** Shortcuts, each shown only when the caller can open the screen (same guards as PeopleHub routes). */
const QUICK_LINKS = [
  { label: 'Attendance', to: '/people/attendance', icon: Clock, perm: ['Attendance', 'Dashboard'] },
  { label: 'Leave', to: '/people/leave', icon: CalendarOff, perm: ['Leave', 'Leave Requests'] },
  { label: 'Payroll', to: '/people/payroll', icon: Wallet, perm: ['Payroll', 'Payroll Records'] },
  { label: 'Shifts', to: '/people/shifts', icon: CalendarClock, perm: ['Shifts', 'Shift Assignments'] },
  { label: 'Holidays', to: '/people/holidays', icon: CalendarDays, perm: ['People', 'Holidays'] },
  { label: 'Employees', to: '/people/employees', icon: Users, perm: ['People', 'Employees'] },
  { label: 'Org chart', to: '/people/orgchart', icon: Network, perm: ['People', 'Org Chart'] },
  { label: 'Recruitment', to: '/people/recruitment', icon: Briefcase, perm: ['Recruitment', 'Jobs'] },
  { label: 'Documents', to: '/people/documents', icon: FileText, perm: ['People', 'Employee Documents'] },
  { label: 'Reports', to: '/people/reports', icon: BarChart3, perm: ['People', 'Reports'] },
] as const;

function QuickAccessPanel({ className }: { className?: string }) {
  const { hasPermission, isPermissionDataAvailable } = useRbacStore();
  // If permissions are unknown, show everything — the route guards still apply.
  const known = isPermissionDataAvailable();
  const links = QUICK_LINKS.filter((l) => !known || hasPermission(l.perm[0], l.perm[1], 'View'));

  return (
    <Panel title="Quick access" className={className}>
      <div className="grid grid-cols-2 gap-2">
        {links.map(({ label, to, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground transition-colors hover:border-primary/40 hover:bg-secondary"
          >
            <Icon className="size-4 text-muted-foreground" />
            <span className="truncate">{label}</span>
          </Link>
        ))}
      </div>
    </Panel>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading People dashboard">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="space-y-3 p-4">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-7 w-32" />
            <Skeleton className="h-3 w-40" />
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <Card className="p-4 lg:col-span-8"><Skeleton className="h-60" /></Card>
        <Card className="p-4 lg:col-span-4"><Skeleton className="h-60" /></Card>
      </div>
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

export function PeopleDashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { data, isLoading, error, fetchDashboard } = usePeopleDashboardStore();

  useEffect(() => {
    fetchDashboard();
    const t = setInterval(() => fetchDashboard({ silent: true }), REFRESH_MS);
    return () => clearInterval(t);
  }, [fetchDashboard]);

  const refresh = () => fetchDashboard({ silent: true });
  const firstName = (data?.me?.employee.name || user?.name || '').split(' ')[0];
  const dateLine = data
    ? parseDay(data.today).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  // The blue "Check in" on the Today card is the dashboard's single primary
  // action, so the header's "Add employee" is a secondary — present for HR,
  // but not competing with the daily check-in for attention.
  const primaryAction = data?.view === 'org'
    ? <Button variant="secondary" onClick={() => navigate('/people/employees/new')}><UserPlus /> Add employee</Button>
    : null;

  return (
    <div className="min-h-full space-y-6 bg-background p-6">
      <PageHeader
        className="mb-0"
        title={firstName ? `${greeting()}, ${firstName}` : 'People'}
        description={data ? `${dateLine} · ${VIEW_LABEL[data.view]}` : 'People & HR overview'}
        actions={
          <>
            <Button variant="secondary" onClick={refresh} disabled={isLoading}>
              <RefreshCw className={cn(isLoading && 'animate-spin')} /> Refresh
            </Button>
            {primaryAction}
          </>
        }
      />

      <CelebrationsBanner bare />

      {error && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive-strong">
          <span className="flex items-center gap-2"><AlertCircle className="size-4" /> {error}</span>
          <Button variant="secondary" size="sm" onClick={() => fetchDashboard()}>Try again</Button>
        </div>
      )}

      {!data && isLoading && <DashboardSkeleton />}

      {data && (
        <>
          <section className="space-y-4">
            <SectionHeading
              title="My day"
              description={data.me
                ? [data.me.employee.designation, data.me.employee.department].filter(Boolean).join(' · ') || undefined
                : undefined}
            />
            {data.me ? (
              <MyDayCards data={data} onChanged={refresh} />
            ) : (
              <div className="flex items-center gap-2 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning-strong">
                <AlertCircle className="size-4 shrink-0" />
                Your account is not linked to an employee profile, so attendance, leave and payslips are unavailable. Please contact HR.
              </div>
            )}
          </section>

          {data.view === 'org' && data.org && <OrgSection data={data} />}
          {data.view === 'team' && <TeamSection data={data} />}

          <section className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            {data.me && <MyWeekPanel me={data.me} today={data.today} className="lg:col-span-4" />}
            <HolidaysPanel data={data} className={data.me ? 'lg:col-span-4' : 'lg:col-span-6'} />
            <QuickAccessPanel className={data.me ? 'lg:col-span-4' : 'lg:col-span-6'} />
          </section>

          {data.me && <MyAssetsPanel />}
        </>
      )}
    </div>
  );
}
